'use client';

import { useRef, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Button } from '@components/ui/button';
import { Input } from '@components/ui/input';
import { useExchangeSignInTicket } from '@lib/auth/hooks/use-exchange-sign-in-ticket';
import { useSendVerificationCode } from '@lib/auth/hooks/use-send-verification-code';
import { VerificationCodeError, useVerifyCode } from '@lib/auth/hooks/use-verify-code';
import { sanitizeRedirectUrl } from '@lib/auth/utils/sanitize-redirect-url';
import { AUTH_ANALYTICS_EVENTS } from '@lib/observability/auth/constants';
import { captureEvent } from '@lib/observability/events/captureEvent';
import { getLogger } from '@lib/observability/logging/log.client';
import { AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { redirectedFromHasSubmissionId } from './redirected-from-has-submission-id';
const {
  logError
} = getLogger('app/(public)/(auth)/login/_components/login-email-code-step');
const EMPTY_DIGITS = ['', '', '', '', '', ''];
interface LoginEmailCodeStepProps {
  email: string;
  redirectedFrom: string | null;
  onBack: () => void;
}
export function LoginEmailCodeStep({
  email,
  redirectedFrom,
  onBack
}: LoginEmailCodeStepProps) {
  const router = useRouter();
  const sendCode = useSendVerificationCode();
  const verifyCode = useVerifyCode();
  const {
    exchangeTicket,
    isExchanging
  } = useExchangeSignInTicket();
  const [digits, setDigits] = useState<string[]>(EMPTY_DIGITS);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const code = digits.join('');
  const isVerifying = verifyCode.isPending || isExchanging;
  async function onVerify(codeOverride?: string) {
    const verifyValue = codeOverride ?? code;
    if (verifyValue.length !== 6) {
      setVerifyError('Please enter the 6-digit code');
      return;
    }
    try {
      const result = await verifyCode.mutateAsync({
        email,
        code: verifyValue
      });
      if (!result.signInToken) {
        throw new Error('Verification succeeded without a sign-in token');
      }
      await exchangeTicket(result.signInToken);
      captureEvent(AUTH_ANALYTICS_EVENTS.LOGIN_EMAIL_CODE_VERIFIED, {
        has_submission_id: redirectedFromHasSubmissionId(redirectedFrom)
      });
      router.replace(sanitizeRedirectUrl(redirectedFrom));
    } catch (err) {
      // Wrong/expired code is expected user behavior — inline only,
      // already Sentry-suppressed by useVerifyCode.
      if (err instanceof VerificationCodeError) {
        setVerifyError(err.message);
        return;
      }

      // Log error so we can investigate email-code sign-in failures
      logError('Email code sign-in failed', err as Error);
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setVerifyError(message);
      toast.error(message);
    }
  }
  const handleDigitChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);
    if (verifyError) setVerifyError(null);
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    if (newDigits.every(d => d !== '') && newDigits.length === 6) {
      setTimeout(() => onVerify(newDigits.join('')), 0);
    }
  };
  const handleKeyDown = (index_0: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index_0] && index_0 > 0) {
      inputRefs.current[index_0 - 1]?.focus();
    }
  };
  const handlePaste = (e_0: React.ClipboardEvent) => {
    e_0.preventDefault();
    const pasted = e_0.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newDigits_0 = [...digits];
    for (let i = 0; i < pasted.length; i++) {
      newDigits_0[i] = pasted[i];
    }
    setDigits(newDigits_0);
    const focusIndex = Math.min(pasted.length, 5);
    inputRefs.current[focusIndex]?.focus();
    if (newDigits_0.every(d_0 => d_0 !== '') && newDigits_0.length === 6) {
      setTimeout(() => onVerify(newDigits_0.join('')), 0);
    }
  };
  async function handleResend() {
    try {
      await sendCode.mutateAsync({
        email,
        redirectedFrom,
        includeMagicLink: true
      });
      setDigits(EMPTY_DIGITS);
      setVerifyError(null);
      inputRefs.current[0]?.focus();
      toast.success('Verification code resent!');
    } catch (err_0) {
      // Log error so we can investigate resend failures
      logError('Failed to resend email sign-in code', err_0 as Error);
      toast.error('Failed to resend code. Please try again.');
    }
  }
  function handleBack() {
    setDigits(EMPTY_DIGITS);
    setVerifyError(null);
    onBack();
  }
  return <>
      <div>
        <Image src="/assets/images/logo.png" className="mx-auto mb-8 h-8 w-8" width={32} height={32} alt="logo" data-sentry-element="Image" data-sentry-source-file="login-email-code-step.tsx" />
        <h1 className="font-ivar text-center text-xl font-normal text-gray-800 md:text-[28px]">
          Check your inbox
        </h1>
        <p className="mx-auto mt-2 max-w-[250px] text-center text-xs text-gray-700 md:text-sm">
          Enter the 6-digit code sent to{' '}
          <span className="font-medium text-gray-800">{email}</span>
        </p>
      </div>

      <div className="mt-6 space-y-6">
        <div className="flex justify-center gap-3">
          {digits.map((digit_0, index_1) => <Input
        // biome-ignore lint/suspicious/noArrayIndexKey: eslint baseline parity
        key={index_1} ref={el => {
          inputRefs.current[index_1] = el;
        }} type="text" inputMode="numeric" autoComplete={index_1 === 0 ? 'one-time-code' : 'off'} maxLength={1} value={digit_0} onChange={e_1 => handleDigitChange(index_1, e_1.target.value)} onKeyDown={e_2 => handleKeyDown(index_1, e_2)} onPaste={index_1 === 0 ? handlePaste : undefined} disabled={isVerifying} className={`h-22 w-15 rounded-[15px] text-center text-[40px] text-black ${verifyError ? 'border border-[#FF6065] bg-[#FEF2F2]' : ''}`} autoFocus={index_1 === 0} />)}
        </div>

        {verifyError ? <div className="flex items-center justify-center gap-1 text-center text-sm text-gray-800">
            <AlertCircle className="h-4 w-4 text-[#B3B3B3]" />
            <span>{verifyError}</span>
          </div> : <div className="h-5"></div>}

        <div className="flex w-full justify-center gap-4">
          <Button variant="secondary-alt" onClick={handleBack} size="lg-alt" data-sentry-element="Button" data-sentry-source-file="login-email-code-step.tsx">
            Back
          </Button>
          <Button onClick={() => onVerify()} disabled={code.length !== 6 ? 'Enter the 6-digit code from your email' : isVerifying} size="lg-alt" data-sentry-element="Button" data-sentry-source-file="login-email-code-step.tsx">
            {isVerifying ? 'Verifying...' : 'Verify code'}
          </Button>
        </div>
      </div>

      <div className="mt-8 text-center">
        <p className="flex items-center justify-center gap-1 text-sm text-gray-600">
          Not received yet?{' '}
          <Button type="button" variant="unstyled" onClick={handleResend} disabled={sendCode.isPending} className="text-sm font-medium text-gray-800 hover:underline" data-sentry-element="Button" data-sentry-source-file="login-email-code-step.tsx">
            Resend code
          </Button>
        </p>
      </div>
    </>;
}